import { useMemo } from "react";

import { safeSync } from "@perceptron/utils/errorHandling";
import { Editor } from "@tiptap/react";

import { CONTENT_RESET_META } from "@demo/extensions/utils/node-protection";
import { Role } from "@demo/types/base";

export interface UseEditorCommandsProps {
  editor: Editor | null;
  editorRole: Role;
}

export interface UseEditorCommandsReturn {
  commands: {
    focus: (position?: "start" | "end" | number) => void;
    clear: () => void;
  };
  isReady: boolean;
}

/**
 * Custom hook that encapsulates the editor commands exposed through the
 * DocumentEditorRef, wrapped with null checks and safeSync error handling.
 */
export function useEditorCommands({ editor, editorRole }: UseEditorCommandsProps): UseEditorCommandsReturn {
  const commands = useMemo(
    () => ({
      focus: (position?: "start" | "end" | number) => {
        safeSync(
          () => {
            if (editor && !editor.isDestroyed) {
              editor.commands.focus(position);
            }
          },
          undefined,
          `EditorCommands.focus.${editorRole}`,
        );
      },

      clear: () => {
        safeSync(
          () => {
            if (editor && !editor.isDestroyed) {
              // contentReset exempts the programmatic wipe from node
              // protection; addToHistory keeps it off the undo stack.
              editor.chain().setMeta(CONTENT_RESET_META, true).setMeta("addToHistory", false).clearContent().run();
            }
          },
          undefined,
          `EditorCommands.clear.${editorRole}`,
        );
      },
    }),
    [editor, editorRole],
  );

  const isReady = Boolean(editor && !editor.isDestroyed);

  return { commands, isReady };
}
